import { useState, useCallback } from "react";

// Non-sensitive per-user UI settings in localStorage ONLY (table density, panel collapse, color tone).
// No records, tokens, PII or secrets are ever stored here; nothing touches the backend/DB.
const KEY = "cargoledger.ui";

function read() {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
}
function write(v) {
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* ignore quota/availability */ }
}

// Scoped UI prefs so each module keeps its own density/collapse without collisions.
export function useUiPrefs(scope = "default") {
  const [prefs, setPrefs] = useState(() => read());
  const get = (k, d) => (prefs?.[scope]?.[k] ?? d);
  const set = useCallback((k, val) => {
    setPrefs((p) => {
      const next = { ...p, [scope]: { ...(p[scope] || {}), [k]: val } };
      write(next);
      return next;
    });
  }, [scope]);
  return {
    density: get("density", "comfortable"),
    setDensity: (v) => set("density", v),
    filtersOpen: get("filtersOpen", true),
    setFiltersOpen: (v) => set("filtersOpen", v),
  };
}

// ---- Color tone (shared CSS-variable themes, NOT separate builds) ----
const THEME_KEY = "cargoledger.theme";
export const THEMES = [
  { id: "ivory", label: "Warm Ivory", swatch: "#0D9488" },
  { id: "slate", label: "Slate Blue", swatch: "#0EA5E9" },
  { id: "charcoal", label: "Charcoal", swatch: "#14B8A6" },
];
const VALID = new Set(THEMES.map((t) => t.id));
const DEFAULT_THEME = "ivory"; // owner-selected default

export function getTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return VALID.has(t) ? t : DEFAULT_THEME;
  } catch { return DEFAULT_THEME; }
}
export function applyTheme(id) {
  const t = VALID.has(id) ? id : DEFAULT_THEME;
  if (typeof document !== "undefined") document.documentElement.setAttribute("data-theme", t);
  try { localStorage.setItem(THEME_KEY, t); } catch { /* ignore */ }
  return t;
}
export function useTheme() {
  const [theme, setThemeState] = useState(getTheme);
  const setTheme = useCallback((id) => setThemeState(applyTheme(id)), []);
  return { theme, setTheme };
}
